import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  ChevronRight,
  Cookie,
  CreditCard,
  FileCheck,
  HelpCircle,
  Mail,
  MessageCircle,
  RefreshCcw,
  ScrollText,
  Search,
  Shield,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LegalFooter from "@/components/legal-footer";

const APP_NAME = "OneNext";
const EDITOR_NAME      = "Odyssée Française";
const EDITOR_CAPITAL   = "10 000";
const EDITOR_RCS_VILLE = "Nîmes";
const EDITOR_RCS       = "Nîmes B 892 445 925";
const EDITOR_SIRET     = "892 445 925 00019";
const EDITOR_TVA       = "FR47892445925";
const EDITOR_ADDRESS   = "86 impasse des Martinets, 30000 Nîmes";
const EDITOR_DIRECTOR  = "Benoît Coadic";
const EDITOR_DPO_EMAIL = "contact@onenext.app";
const SUPPORT_EMAIL_FALLBACK = "support@onenext.app";
const LAST_UPDATED = "3 mai 2026";

type Slug =
  | "cgu"
  | "cgv"
  | "confidentialite"
  | "mentions"
  | "cookies"
  | "remboursement"
  | "dpa";

const TITLES: Record<Slug, string> = {
  cgu: "Conditions générales d'utilisation",
  cgv: "Conditions générales de vente",
  confidentialite: "Politique de confidentialité",
  mentions: "Mentions légales",
  cookies: "Politique cookies",
  remboursement: "Politique de remboursement",
  dpa: "Accord de traitement des données (DPA)",
};

const SLUG_ORDER: Slug[] = [
  "mentions",
  "cgu",
  "cgv",
  "confidentialite",
  "cookies",
  "remboursement",
  "dpa",
];

function isSlug(v: string | undefined): v is Slug {
  return !!v && (Object.keys(TITLES) as Slug[]).includes(v as Slug);
}

export default function LegalPage() {
  const [, params] = useRoute<{ slug: string }>("/legal/:slug");
  const slug = params?.slug;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!isSlug(slug)) {
    return <LegalIndex />;
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      <header className="border-b">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Accueil
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
              O
            </div>
            {APP_NAME}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-6 py-10 prose prose-neutral dark:prose-invert">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold m-0">
            Documents légaux
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight">
            {TITLES[slug]}
          </h1>
          <p className="text-sm text-muted-foreground">
            Dernière mise à jour : {LAST_UPDATED}
          </p>
          <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-foreground">
            {renderContent(slug)}
          </div>
          <nav className="mt-12 pt-6 border-t flex flex-wrap gap-3 text-sm">
            <span className="text-muted-foreground">Voir aussi :</span>
            {SLUG_ORDER.filter((s) => s !== slug).map((s) => (
              <Link
                key={s}
                href={`/legal/${s}`}
                className="text-primary hover:underline"
              >
                {TITLES[s]}
              </Link>
            ))}
          </nav>
        </article>
      </main>
      <LegalFooter />
    </div>
  );
}

const LEGAL_CARDS: {
  slug: Slug;
  icon: React.ElementType;
  description: string;
  color: string;
}[] = [
  {
    slug: "mentions",
    icon: Building2,
    description: "Identité de l'éditeur, hébergeur et propriété intellectuelle.",
    color: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
  },
  {
    slug: "cgu",
    icon: ScrollText,
    description: "Règles d'utilisation du service, droits et obligations.",
    color: "text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-300",
  },
  {
    slug: "cgv",
    icon: CreditCard,
    description: "Tarifs, commandes, paiement et résiliation.",
    color: "text-violet-600 bg-violet-50 dark:bg-violet-950 dark:text-violet-300",
  },
  {
    slug: "confidentialite",
    icon: Shield,
    description: "Données collectées, finalités de traitement et vos droits RGPD.",
    color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300",
  },
  {
    slug: "cookies",
    icon: Cookie,
    description: "Traceurs utilisés sur le site et gestion du consentement.",
    color: "text-amber-600 bg-amber-50 dark:bg-amber-950 dark:text-amber-300",
  },
  {
    slug: "remboursement",
    icon: RefreshCcw,
    description: "Conditions de remboursement et politique d'annulation.",
    color: "text-rose-600 bg-rose-50 dark:bg-rose-950 dark:text-rose-300",
  },
  {
    slug: "dpa",
    icon: FileCheck,
    description: "Accord de traitement des données (sous-traitant IA et hébergement).",
    color: "text-cyan-600 bg-cyan-50 dark:bg-cyan-950 dark:text-cyan-300",
  },
];

function LegalIndex() {
  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      {/* ── Navbar ─────────────────────────────────────────────────── */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-5xl px-6 py-3 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <div className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
                O
              </div>
              {APP_NAME}
            </div>
          </Link>
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
              <ArrowLeft className="w-3.5 h-3.5" /> Accueil
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* ── Hero ───────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-b from-primary/8 via-primary/3 to-background border-b">
          <div className="mx-auto max-w-5xl px-6 py-14 text-center flex flex-col items-center gap-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1 uppercase tracking-widest">
              <Zap className="w-3 h-3" /> Centre d'aide
            </span>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Comment pouvons-nous vous aider ?
            </h1>
            <p className="text-muted-foreground max-w-md">
              Retrouvez tous les documents juridiques, notre politique de confidentialité et contactez notre équipe.
            </p>
            {/* Search bar (decorative — sera connecté côté support) */}
            <div className="w-full max-w-lg relative mt-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="search"
                placeholder="Rechercher un document, une question…"
                className="w-full pl-10 pr-4 py-2.5 rounded-full border bg-background text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
              />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6 py-12 space-y-14">

          {/* ── Documents légaux ───────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight">Documents légaux</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {LEGAL_CARDS.length} documents applicables à {APP_NAME}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {LEGAL_CARDS.map(({ slug, icon: Icon, description, color }) => (
                <Link key={slug} href={`/legal/${slug}`}>
                  <div className="group h-full flex flex-col gap-3 p-5 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
                    <div className="flex items-start justify-between">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-1" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm leading-tight">{TITLES[slug]}</p>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
                    </div>
                    <div className="text-[11px] text-muted-foreground/60 font-mono">
                      /legal/{slug}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* ── Support & contact ──────────────────────────────────── */}
          <section>
            <div className="mb-6">
              <h2 className="text-xl font-bold tracking-tight">Support & contact</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Besoin d'aide ? Notre équipe répond rapidement.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Contact form card */}
              <Link href="/contact">
                <div className="group flex items-start gap-4 p-5 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">Formulaire de contact</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Envoyez-nous un message. Réponse sous 48h ouvrées (Pro) ou 24h (Unlimited).
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
                </div>
              </Link>

              {/* Email card */}
              <a href={`mailto:${SUPPORT_EMAIL_FALLBACK}`}>
                <div className="group flex items-start gap-4 p-5 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-300 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">E-mail direct</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Écrivez-nous à{" "}
                      <span className="text-primary font-medium">{SUPPORT_EMAIL_FALLBACK}</span>
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
                </div>
              </a>

              {/* FAQ card */}
              <Link href="/pricing#faq">
                <div className="group flex items-start gap-4 p-5 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-300 flex items-center justify-center shrink-0">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">Questions fréquentes</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Tarifs, sécurité, RGPD, fonctionnalités — toutes les réponses.
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
                </div>
              </Link>

              {/* Changelog card */}
              <Link href="/changelog">
                <div className="group flex items-start gap-4 p-5 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-300 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">Nouveautés & changelog</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Suivez les dernières mises à jour et améliorations du produit.
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
                </div>
              </Link>
            </div>
          </section>

          {/* ── RGPD callout ───────────────────────────────────────── */}
          <section className="rounded-2xl border bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Vos données vous appartiennent</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Exportez ou supprimez vos données à tout moment depuis{" "}
                <Link href="/settings" className="text-primary hover:underline">
                  Réglages → Mes données (RGPD)
                </Link>.
              </p>
            </div>
            <Link href="/legal/confidentialite">
              <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                Politique de confidentialité <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </section>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

// ─── Section helpers ──────────────────────────────────────────────────

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-semibold tracking-tight mt-8 mb-2">
      {children}
    </h2>
  );
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="my-3 text-foreground/90">{children}</p>;
}
function UL({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-6 my-3 space-y-1.5">{children}</ul>;
}

// ─── Content ──────────────────────────────────────────────────────────

function renderContent(slug: Slug) {
  switch (slug) {
    case "mentions":
      return <MentionsContent />;
    case "cgu":
      return <CguContent />;
    case "cgv":
      return <CgvContent />;
    case "confidentialite":
      return <ConfidentialiteContent />;
    case "cookies":
      return <CookiesContent />;
    case "remboursement":
      return <RemboursementContent />;
    case "dpa":
      return <DpaContent />;
  }
}

function RemboursementContent() {
  return (
    <>
      <H2>1. Principe général</H2>
      <P>
        {APP_NAME} est un service numérique en ligne dont l'exécution
        commence dès la souscription d'une offre payante. Conformément à
        l'article L221-28 13° du Code de la consommation, le droit de
        rétractation ne s'applique pas aux contrats de fourniture de
        contenu numérique non fourni sur un support matériel dont
        l'exécution a commencé après accord exprès du consommateur.
      </P>
      <P>
        Lors de la souscription, vous donnez votre consentement exprès au
        démarrage immédiat du service et reconnaissez perdre votre droit
        de rétractation. Aucun remboursement n'est donc dû au titre du
        droit de rétractation des 14 jours.
      </P>
      <H2>2. Résiliation et fin d'abonnement</H2>
      <P>
        Vous pouvez résilier votre abonnement à tout moment depuis
        Réglages → Forfait & crédits, sans aucun frais ni justification.
        La résiliation prend effet à la fin de la période en cours
        (mensuelle) et vous conservez l'accès complet au service
        jusqu'à cette date.
      </P>
      <P>
        Aucun remboursement prorata temporis n'est accordé pour la
        période d'abonnement déjà entamée.
      </P>
      <H2>3. Packs de crédits IA</H2>
      <P>
        Les packs de crédits ponctuels (100, 500 ou 1 000 crédits) sont
        consommables à votre rythme. Les crédits déjà consommés ne sont
        pas remboursables. Les crédits non consommés peuvent faire
        l'objet d'un remboursement dans les 14 jours suivant l'achat,
        sur demande motivée auprès du support, et uniquement si aucun
        crédit du pack n'a été utilisé.
      </P>
      <H2>4. Cas exceptionnels donnant lieu à remboursement</H2>
      <P>
        Un remboursement total ou partiel peut être accordé dans les cas
        suivants :
      </P>
      <UL>
        <li>
          Indisponibilité prolongée du service (interruption continue de
          plus de 72 heures imputable à l'éditeur), au prorata de la
          durée d'indisponibilité ;
        </li>
        <li>
          Double facturation ou erreur technique de paiement ;
        </li>
        <li>
          Souscription effectuée par erreur et signalée dans les
          48 heures, à condition qu'aucun usage significatif du service
          n'ait été constaté.
        </li>
      </UL>
      <H2>5. Comment demander un remboursement</H2>
      <P>
        Toute demande de remboursement doit être adressée par e-mail à
        l'adresse de support indiquée dans les{" "}
        <Link href="/legal/mentions" className="text-primary hover:underline">
          Mentions légales
        </Link>
        {" "}ou via le{" "}
        <Link href="/contact" className="text-primary hover:underline">
          formulaire de contact
        </Link>
        , en précisant :
      </P>
      <UL>
        <li>l'adresse e-mail associée au compte OneNext ;</li>
        <li>la date et le montant du paiement concerné ;</li>
        <li>le motif détaillé de la demande.</li>
      </UL>
      <P>
        Une réponse vous est apportée sous 5 jours ouvrés. Lorsqu'un
        remboursement est accepté, il est effectué sur le moyen de
        paiement initial sous 14 jours.
      </P>
      <H2>6. Médiation de la consommation</H2>
      <P>
        En cas de litige persistant, le consommateur peut, conformément
        à l'article L612-1 du Code de la consommation, recourir
        gratuitement au service de médiation indiqué dans les{" "}
        <Link href="/legal/cgv" className="text-primary hover:underline">
          Conditions générales de vente
        </Link>
        .
      </P>
    </>
  );
}

function MentionsContent() {
  return (
    <>
      <H2>Éditeur du site</H2>
      <P>
        Le service {APP_NAME} est édité par {EDITOR_NAME} (raison
        sociale), société {EDITOR_NAME} au capital de
        {" "}
        {EDITOR_CAPITAL} euros, immatriculée au RCS de
        {" "}
        {EDITOR_RCS_VILLE} sous le numéro SIRET {EDITOR_SIRET},
        dont le siège social est situé {EDITOR_ADDRESS}.
      </P>
      <P>
        Numéro de TVA intracommunautaire : {EDITOR_TVA}.
        <br />
        Directeur de la publication : {EDITOR_DIRECTOR}.
        <br />
        Contact : <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL_FALLBACK}`}>{SUPPORT_EMAIL_FALLBACK}</a>
        {" "}
        ou via le{" "}
        <Link href="/contact" className="text-primary hover:underline">
          formulaire de contact
        </Link>
        .
      </P>
      <H2>Hébergement</H2>
      <P>
        L'application est hébergée par Replit Inc., 548 Market St #94099,
        San Francisco, CA 94104, États-Unis. L'infrastructure d'hébergement
        s'appuie sur Google Cloud Platform (région Europe).
      </P>
      <H2>Propriété intellectuelle</H2>
      <P>
        L'ensemble des éléments du site (textes, logos, graphismes, code
        source) est protégé par le droit d'auteur. Toute reproduction,
        représentation ou diffusion, totale ou partielle, sans
        autorisation écrite préalable de l'éditeur, est interdite.
      </P>
      <H2>Crédits</H2>
      <P>
        Icônes : Lucide. Police : Plus Jakarta Sans (Google Fonts).
        Authentification : Clerk. Modèles d'IA : OpenAI.
      </P>
    </>
  );
}

function CguContent() {
  return (
    <>
      <H2>1. Objet</H2>
      <P>
        Les présentes conditions générales d'utilisation (CGU) régissent
        l'accès et l'usage de {APP_NAME}, application en ligne d'aide à
        l'organisation personnelle, à la gestion de tâches, de projets et
        de boîtes mail. Tout accès au service implique l'acceptation
        sans réserve de ces CGU.
      </P>
      <H2>2. Inscription et compte</H2>
      <P>
        L'inscription s'effectue via Clerk. L'utilisateur s'engage à
        fournir des informations exactes et à conserver la confidentialité
        de ses moyens d'authentification. L'accès est strictement
        personnel.
      </P>
      <H2>3. Description du service</H2>
      <P>
        {APP_NAME} permet à l'utilisateur de :
      </P>
      <UL>
        <li>capturer et organiser des tâches et projets ;</li>
        <li>connecter des boîtes mail (IMAP/SMTP) pour les consulter et y répondre ;</li>
        <li>obtenir un classement automatique de ses e-mails et des suggestions d'actions générées par intelligence artificielle ;</li>
        <li>discuter avec un assistant conversationnel pour planifier sa journée.</li>
      </UL>
      <H2>4. Disponibilité</H2>
      <P>
        L'éditeur s'efforce de maintenir le service accessible 24h/24,
        sans pour autant pouvoir garantir une disponibilité absolue. Des
        interruptions peuvent intervenir pour maintenance, mises à jour,
        ou en cas de force majeure. Aucune indemnité ne pourra être
        exigée à raison de ces interruptions.
      </P>
      <H2>5. Comportement de l'utilisateur</H2>
      <P>
        L'utilisateur s'engage à :
      </P>
      <UL>
        <li>ne pas tenter d'accéder aux données d'autres utilisateurs ;</li>
        <li>ne pas chercher à porter atteinte à la sécurité ou à l'intégrité du service ;</li>
        <li>ne pas envoyer de contenus illégaux, frauduleux ou violant les droits de tiers ;</li>
        <li>respecter les conditions d'utilisation des fournisseurs tiers (notamment les fournisseurs de messagerie connectés).</li>
      </UL>
      <H2>6. Intelligence artificielle</H2>
      <P>
        Certaines fonctionnalités s'appuient sur des modèles d'IA tiers
        (notamment OpenAI). Les contenus envoyés à ces modèles peuvent
        être analysés par le sous-traitant pour rendre le service. Le
        détail de ces traitements figure dans la{" "}
        <Link href="/legal/confidentialite" className="text-primary hover:underline">
          Politique de confidentialité
        </Link>
        . Les résultats produits par l'IA sont fournis à titre indicatif
        et ne sauraient se substituer à l'appréciation de l'utilisateur.
      </P>
      <H2>7. Suspension et résiliation</H2>
      <P>
        L'éditeur se réserve le droit de suspendre ou résilier l'accès
        d'un utilisateur en cas de violation des présentes CGU.
        L'utilisateur peut à tout moment supprimer son compte depuis
        Réglages → Mes données (RGPD).
      </P>
      <H2>8. Responsabilité</H2>
      <P>
        L'éditeur ne saurait être tenu responsable de la perte de
        données, des dysfonctionnements liés aux services tiers
        (hébergeur, fournisseurs de messagerie, fournisseurs d'IA), ou
        de l'usage que l'utilisateur fait du service.
      </P>
      <H2>9. Modification des CGU</H2>
      <P>
        Les CGU peuvent être modifiées à tout moment. Les utilisateurs
        sont informés des modifications substantielles par e-mail ou
        notification dans l'application au moins 30 jours avant leur
        entrée en vigueur.
      </P>
      <H2>10. Droit applicable</H2>
      <P>
        Les présentes CGU sont soumises au droit français. Tout litige
        sera porté devant les tribunaux compétents de
        {" "}
        {EDITOR_RCS_VILLE}, sous réserve des dispositions impératives
        du droit de la consommation.
      </P>
    </>
  );
}

function CgvContent() {
  return (
    <>
      <H2>1. Champ d'application</H2>
      <P>
        Les présentes conditions générales de vente (CGV) régissent les
        offres payantes de {APP_NAME}. Elles complètent les CGU et
        prévalent sur toute autre condition non expressément acceptée
        par l'éditeur.
      </P>
      <H2>2. Offres et tarifs</H2>
      <P>
        {APP_NAME} propose une offre gratuite (Free) avec des crédits
        d'IA limités, et une ou plusieurs offres payantes (Pro,
        Unlimited, achats de crédits supplémentaires) dont le détail et
        le prix figurent à tout moment sur la page Réglages → Forfait &
        crédits. Les prix sont indiqués TTC en euros.
      </P>
      <H2>3. Commande</H2>
      <P>
        La souscription à une offre payante s'effectue depuis l'espace
        connecté. La commande est validée à l'issue d'un récapitulatif
        et du paiement.
      </P>
      <H2>4. Paiement</H2>
      <P>
        Le paiement est sécurisé via le prestataire Stripe
        {" "}
        (Stripe, Inc.). Aucune donnée bancaire n'est stockée sur les
        serveurs de l'éditeur. Le débit intervient à la souscription
        puis à chaque renouvellement automatique pour les offres par
        abonnement.
      </P>
      <H2>5. Durée et renouvellement</H2>
      <P>
        Les abonnements mensuels sont conclus pour une durée d'un mois
        et renouvelés tacitement chaque mois jusqu'à résiliation par
        l'utilisateur depuis son espace personnel.
      </P>
      <H2>6. Droit de rétractation</H2>
      <P>
        Conformément à l'article L221-28 du Code de la consommation, le
        droit de rétractation ne peut être exercé pour les contrats de
        fourniture de contenus numériques non fournis sur un support
        matériel dont l'exécution a commencé après accord préalable
        exprès de l'utilisateur. Lors de la souscription, l'utilisateur
        renonce expressément à son droit de rétractation pour bénéficier
        immédiatement du service.
      </P>
      <H2>7. Résiliation</H2>
      <P>
        L'utilisateur peut résilier son abonnement à tout moment depuis
        Réglages → Forfait & crédits. La résiliation prend effet à la
        fin de la période en cours ; aucun remboursement prorata
        temporis n'est accordé.
      </P>
      <H2>8. Crédits IA</H2>
      <P>
        Les crédits d'IA achetés ou inclus dans un forfait sont
        consommés à chaque appel à un modèle d'intelligence
        artificielle. Les crédits non consommés à la fin d'un cycle de
        facturation expirent sauf mention contraire dans l'offre.
      </P>
      <H2>9. Service client</H2>
      <P>
        Pour toute question relative à une commande, l'utilisateur peut
        contacter le support à l'adresse{" "}
        <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL_FALLBACK}`}>{SUPPORT_EMAIL_FALLBACK}</a>
        {" "}ou via le{" "}
        <Link href="/contact" className="text-primary hover:underline">
          formulaire de contact
        </Link>
        .
      </P>
      <H2>10. Médiation de la consommation</H2>
      <P>
        Conformément à l'article L612-1 du Code de la consommation, le
        consommateur peut recourir gratuitement au service de médiation
        de la consommation désigné par {EDITOR_NAME} sur simple demande
        à {EDITOR_DPO_EMAIL}.
      </P>
    </>
  );
}

function ConfidentialiteContent() {
  return (
    <>
      <H2>1. Responsable de traitement</H2>
      <P>
        Le responsable du traitement des données personnelles collectées
        sur {APP_NAME} est {EDITOR_NAME} (cf. Mentions légales).
        Le délégué à la protection des données peut être contacté à
        l'adresse {EDITOR_DPO_EMAIL}.
      </P>
      <H2>2. Données collectées</H2>
      <UL>
        <li><strong>Compte</strong> : adresse e-mail, prénom/nom, identifiant Clerk.</li>
        <li><strong>Contenu</strong> : tâches, projets, notes, événements créés par l'utilisateur.</li>
        <li><strong>Boîtes mail connectées</strong> : identifiants IMAP/SMTP (mots de passe chiffrés au repos en AES-256-GCM), en-têtes et contenus des e-mails synchronisés, pièces jointes.</li>
        <li><strong>Usage de l'IA</strong> : compteurs d'utilisation, coûts, contenus envoyés au sous-traitant IA pour analyse.</li>
        <li><strong>Logs techniques</strong> : adresse IP, horodatages, type d'évènement, à des fins de sécurité et de prévention de la fraude.</li>
        <li><strong>Facturation</strong> : forfait souscrit, historique des évènements de facturation.</li>
      </UL>
      <H2>3. Finalités et bases juridiques</H2>
      <UL>
        <li>Fourniture du service (exécution du contrat) ;</li>
        <li>Facturation et obligations comptables (obligation légale) ;</li>
        <li>Sécurité, prévention de la fraude (intérêt légitime) ;</li>
        <li>Mémorisation du consentement cookies (consentement) ;</li>
        <li>Connexion d'une boîte mail tierce (consentement explicite à chaque ajout).</li>
      </UL>
      <H2>4. Sous-traitants principaux</H2>
      <P>
        L'éditeur a recours aux sous-traitants suivants, qui présentent
        des garanties suffisantes au sens de l'article 28 du RGPD :
      </P>
      <UL>
        <li><strong>Clerk Inc.</strong> (États-Unis) — authentification ; clauses contractuelles types.</li>
        <li><strong>OpenAI Ireland Ltd</strong> — traitements d'IA (analyse d'e-mails, suggestions de tâches, transcription audio, assistant conversationnel). Les données envoyées via l'API OpenAI ne sont pas utilisées pour entraîner les modèles. Conservation maximale chez OpenAI : 30 jours pour modération.</li>
        <li><strong>Replit Inc. / Google Cloud (Europe)</strong> — hébergement applicatif et base de données.</li>
        <li><strong>Fournisseurs de messagerie</strong> : choisis par l'utilisateur lors de la connexion d'une boîte mail.</li>
      </UL>
      <H2>5. Transferts hors UE</H2>
      <P>
        Certains sous-traitants (Clerk, OpenAI) opèrent depuis les
        États-Unis. Les transferts sont encadrés par les clauses
        contractuelles types adoptées par la Commission européenne.
      </P>
      <H2>6. Durées de conservation</H2>
      <UL>
        <li>Données de compte : durée du compte + 30 jours ;</li>
        <li>Tâches, projets, notes, événements : suppression immédiate à la demande de l'utilisateur ou à la suppression du compte ;</li>
        <li>E-mails synchronisés : effacés à la déconnexion du compte mail ou à la suppression du compte OneNext ;</li>
        <li>Logs d'activité applicatifs : 90 jours ;</li>
        <li>Journal d'audit administrateur : 12 mois ;</li>
        <li>Données de facturation : 10 ans (obligation comptable).</li>
      </UL>
      <H2>7. Vos droits</H2>
      <P>
        Conformément aux articles 15 à 22 du RGPD, l'utilisateur dispose
        des droits d'accès, de rectification, d'effacement, de
        limitation, d'opposition et de portabilité.
      </P>
      <P>
        Deux droits peuvent être exercés directement depuis Réglages → Mes
        données (RGPD) :
      </P>
      <UL>
        <li><strong>Article 20 (portabilité)</strong> : export complet au format JSON de toutes les données associées au compte.</li>
        <li><strong>Article 17 (effacement)</strong> : suppression définitive du compte et de l'ensemble des données associées.</li>
      </UL>
      <P>
        Pour les autres droits, contactez l'éditeur via le{" "}
        <Link href="/contact" className="text-primary hover:underline">
          formulaire de contact
        </Link>
        . En cas de litige, l'utilisateur peut introduire une réclamation
        auprès de la CNIL (
        <a
          className="text-primary hover:underline"
          href="https://www.cnil.fr"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.cnil.fr
        </a>
        ).
      </P>
      <H2>8. Sécurité</H2>
      <P>
        Les communications HTTP sont chiffrées en TLS. Les mots de passe
        IMAP/SMTP des boîtes mail connectées sont chiffrés au repos via
        AES-256-GCM. L'authentification est gérée par Clerk avec MFA
        optionnel. Les actions administrateur font l'objet d'un journal
        d'audit dédié.
      </P>
      <H2>9. Mineurs</H2>
      <P>
        Le service n'est pas destiné aux personnes de moins de 16 ans.
        L'éditeur ne collecte pas sciemment de données concernant des
        mineurs.
      </P>
    </>
  );
}

function CookiesContent() {
  return (
    <>
      <H2>1. Qu'est-ce qu'un cookie ?</H2>
      <P>
        Un cookie est un petit fichier texte déposé sur votre terminal
        (ordinateur, smartphone, tablette) lors de la visite d'un site
        web. Il permet au site de mémoriser des informations relatives à
        votre navigation.
      </P>
      <H2>2. Les cookies utilisés par {APP_NAME}</H2>
      <P>
        {APP_NAME} utilise uniquement des cookies et identifiants de
        stockage local strictement nécessaires au fonctionnement du
        service. Aucun cookie publicitaire ni cookie de mesure
        d'audience tierce n'est déposé.
      </P>
      <UL>
        <li>
          <strong>Cookies d'authentification (Clerk)</strong> :
          indispensables pour maintenir votre session connectée.
          Conservés le temps de la session et jusqu'à la déconnexion.
        </li>
        <li>
          <strong>Stockage local des préférences UI</strong> : choix
          d'affichage, accordéons ouverts/fermés. Conservé jusqu'à
          12 mois.
        </li>
        <li>
          <strong>Mémorisation du consentement cookies</strong> :
          enregistrement de votre choix sur la bannière de consentement
          (clé <code>onenext.cookie-consent.v1</code>). Conservé 12 mois.
        </li>
      </UL>
      <H2>3. Gestion du consentement</H2>
      <P>
        Une bannière de consentement s'affiche lors de votre première
        visite. Vous pouvez à tout moment effacer le stockage local de
        votre navigateur pour réinitialiser votre consentement, ou
        ajuster les permissions cookies dans les paramètres de votre
        navigateur.
      </P>
      <H2>4. Cookies déposés par les sous-traitants</H2>
      <P>
        Lorsque vous vous authentifiez, des cookies peuvent être déposés
        par Clerk (sous-traitant authentification). Ces cookies sont
        strictement nécessaires à la connexion sécurisée.
      </P>
    </>
  );
}

function DpaContent() {
  return (
    <>
      <div className="mb-6 rounded-xl border bg-card p-4 flex items-start gap-3 not-prose">
        <div className="text-sm flex-1">
          <p className="font-semibold">Version téléchargeable</p>
          <p className="text-muted-foreground mt-1">
            Téléchargez ce DPA au format Markdown pour vos archives de
            conformité.
          </p>
        </div>
        <a
          href={`${import.meta.env.BASE_URL}api/legal/dpa.md`}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          data-testid="legal-dpa-download"
        >
          Télécharger le DPA
        </a>
      </div>
      <P>
        Le présent accord (Data Processing Agreement, DPA) précise les
        rôles et responsabilités des parties au regard du Règlement
        général sur la protection des données (RGPD).
      </P>
      <H2>1. Parties</H2>
      <P>
        <strong>Responsable de traitement</strong> : l'utilisateur
        professionnel ou l'organisation utilisant {APP_NAME} pour ses
        propres finalités.
        <br />
        <strong>Sous-traitant</strong> : {EDITOR_NAME}, éditeur de
        {" "}
        {APP_NAME}, agissant pour le compte du Responsable de
        traitement dans la limite de ses instructions et des
        fonctionnalités proposées par le service.
      </P>
      <H2>2. Objet du traitement</H2>
      <P>
        Le sous-traitant traite les données pour fournir les
        fonctionnalités de gestion de tâches, projets, e-mails et
        assistance par IA décrites dans les CGU.
      </P>
      <H2>3. Durée</H2>
      <P>
        Le présent DPA prend effet à la création du compte et reste en
        vigueur jusqu'à la résiliation du contrat ou la suppression du
        compte.
      </P>
      <H2>4. Catégories de données et de personnes concernées</H2>
      <P>
        Données identifiantes (nom, e-mail), contenus créés
        (tâches/projets/notes), e-mails synchronisés, métadonnées
        d'usage. Personnes concernées : l'utilisateur et ses
        correspondants e-mails.
      </P>
      <H2>5. Obligations du sous-traitant</H2>
      <UL>
        <li>Traiter les données uniquement sur instruction documentée du Responsable et conformément aux finalités décrites ;</li>
        <li>Garantir la confidentialité, notamment par les engagements du personnel ;</li>
        <li>Mettre en œuvre les mesures de sécurité appropriées (TLS, chiffrement au repos, contrôles d'accès, audit log) ;</li>
        <li>Notifier toute violation de données dans un délai de 72 heures après en avoir pris connaissance ;</li>
        <li>Assister le Responsable dans la réponse aux demandes d'exercice de droits et dans la réalisation des analyses d'impact.</li>
      </UL>
      <H2>6. Sous-traitants ultérieurs</H2>
      <P>
        Le Responsable autorise le recours aux sous-traitants suivants :
      </P>
      <UL>
        <li>Clerk Inc. — authentification ;</li>
        <li>OpenAI Ireland Ltd — traitements d'IA (les données ne sont pas utilisées pour entraîner les modèles ; conservation 30 jours max) ;</li>
        <li>Replit Inc. / Google Cloud (Europe) — hébergement.</li>
      </UL>
      <P>
        Tout nouveau sous-traitant est annoncé avec un préavis raisonnable
        permettant au Responsable de s'y opposer.
      </P>
      <H2>7. Transferts hors UE</H2>
      <P>
        Les transferts vers les États-Unis (Clerk, OpenAI) sont encadrés
        par les clauses contractuelles types de la Commission
        européenne.
      </P>
      <H2>8. Sort des données en fin de contrat</H2>
      <P>
        À la suppression du compte, l'ensemble des données est supprimé
        dans un délai maximum de 30 jours, sous réserve des obligations
        légales de conservation (notamment comptables).
      </P>
      <H2>9. Audit</H2>
      <P>
        Le Responsable peut demander, une fois par an, communication
        des éléments documentaires nécessaires à la démonstration du
        respect du présent DPA.
      </P>
      <H2>10. Acceptation</H2>
      <P>
        L'utilisation du service emporte acceptation du présent DPA.
        Pour la signature d'un DPA dédié, contactez l'éditeur à{" "}
        <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL_FALLBACK}`}>{SUPPORT_EMAIL_FALLBACK}</a>.
      </P>
    </>
  );
}
